import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, RotateCcw, Users, FileText
} from 'lucide-react';
import { toast } from 'react-toastify';
import { accountsService } from '../../../services/accountsService';
import customerMasterService from '../../../services/customerMasterService';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import DataTable from '../../../components/DataTable/DataTable';

const SundryDebtorsReport = () => {
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState({
    customer: '',
    asOfDate: new Date().toISOString().split('T')[0] // Today's date
  });
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await customerMasterService.getAllCustomers({ limit: 1000 });
        if (response && response.success) {
          setCustomers(response.data || []);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        asOfDate: filters.asOfDate
      };
      
      if (filters.customer) {
        params.customer = filters.customer;
      }
      
      const response = await accountsService.getSundryDebtorsReport(params);

      if (response && response.success) {
        setReportData(response.data);
      } else {
        toast.error('Failed to load sundry debtors report');
      }
    } catch (error) {
      console.error('Error loading sundry debtors report:', error);
      toast.error('Failed to load sundry debtors report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetFilter = () => {
    setFilters({
      customer: '',
      asOfDate: new Date().toISOString().split('T')[0]
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Font sizes for report - matching Tax Invoice Report
  const reportFontSize = {
    label: '11px',
    input: '11px',
    footerLabel: '14px',
    footerValue: '20px'
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'serialNumber',
      label: 'SN',
      sortable: false,
      render: (value) => value
    },
    {
      key: 'customerName',
      label: 'NAME OF PARTY & ADDRESS',
      sortable: true,
      render: (value, row) => {
        return (
          <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              {row.customerName || '-'}
            </div>
            {row.customerAddress && (
              <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)' }}>
                {row.customerAddress}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'invoiceDate',
      label: 'INV DATE',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'invoiceNumber',
      label: 'INV NO.',
      sortable: true
    },
    {
      key: 'invoiceAmount',
      label: 'INV AMT',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'tdsAmount',
      label: 'TDS',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'amountReceivable',
      label: 'AMT RECIVABLE',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'overdueDays',
      label: 'OVERDUE BY DAYS',
      sortable: true,
      render: (value) => value || 0
    }
  ];

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Accounts',
      path: '/accounts',
      onClick: () => navigate('/accounts')
    },
    {
      label: 'Reports',
      path: '/accounts?tab=reports',
      onClick: () => navigate('/accounts?tab=reports')
    },
    {
      label: 'Sundry Debtors Report',
      path: '/accounts/reports/sundry-debtors',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '22px' }}>Sundry Debtors Report</h1>
          <p style={{ fontSize: '12px' }}>Track outstanding receivables from customers</p>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Select Customer</label>
            <select
              className="form-input"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer._id || customer.id} value={customer._id || customer.id}>
                  {customer.customerName} ({customer.customerCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>As Of Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.asOfDate}
              onChange={(e) => handleFilterChange('asOfDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <button
            type="button"
            onClick={loadReportData}
            className="btn btn-primary"
            style={{ 
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#ec4899',
              borderColor: '#ec4899'
            }}
            disabled={loading}
          >
            <Filter size={16} />
            Apply Filter
          </button>

          <button
            type="button"
            onClick={handleResetFilter}
            className="btn btn-secondary"
            style={{ 
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RotateCcw size={16} />
            Reset Filter
          </button>
        </div>
      </div>

      {/* Data Table - Direct in parent container, no wrapper */}
      <div style={{ 
        marginBottom: '80px', 
        marginLeft: '0', 
        marginRight: '0',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading sundry debtors report...</div>
          </div>
        ) : reportData && reportData.invoices && reportData.invoices.length > 0 ? (
          <DataTable
            data={reportData.invoices}
            columns={columns}
            loading={loading}
            pagination={true}
            pageSize={50}
            noCard={true}
            className="sundry-debtors-report-table"
            title=""
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Users size={48} style={{ color: 'var(--color-text-muted, #8B93A7)', marginBottom: '16px' }} />
            <div style={{ color: 'var(--color-text-secondary, #5B6478)' }}>No outstanding receivables found</div>
          </div>
        )}
      </div>

      {/* Summary Footer - Fixed at bottom, matching table width */}
      {reportData && reportData.summary && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: '314px', // Sidebar (250px) + left padding (64px)
          right: '64px', // Right padding to match sales-dashboard
          background: '#E8E1F5',
          padding: '14px 20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          zIndex: 100,
          borderRadius: '0',
          borderTop: '2px solid #E8E1F5'
        }}>
          <div>
            <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total Invoice Amt
            </div>
            <div style={{ fontSize: reportFontSize.footerValue, fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalInvoiceAmount)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total TDS Amt
            </div>
            <div style={{ fontSize: reportFontSize.footerValue, fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalTDSAmount)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total Recivable Amt
            </div>
            <div style={{ fontSize: reportFontSize.footerValue, fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalReceivableAmount)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SundryDebtorsReport;

